import BaseModal from 'components/Modals/BaseModal';
import { Alert, Custom_Alerts } from 'features/Weather/features/WeatherAlerts/types/alerts.types';
import { useState } from 'react';
import { Button } from 'react-bootstrap';
import { useGetGlobalActionsQuery } from 'services/globalActions/globalActions.api';
import { formatDateTime } from 'utils/dates/date.utils';
import { CloseAlertModalProps } from './CloseAlertModal.types';

/**
 *
 * @param alert
 * @returns {Boolean} true if the alert is a Custom Alert with a workplaceId, false otherwise
 */
const isCustomAlert = (alert: Alert): boolean => {
  return (
    alert != null &&
    typeof (alert as Custom_Alerts).workplaceId !== 'undefined' &&
    (alert as Custom_Alerts).workplaceId !== null
  );
};

const canSubmit = (notes: string): boolean => {
  // Require notes to be non-empty before allowing submission
  return notes.trim().length > 0;
};

const CloseAlertModal = ({ show, alert, onClose, onConfirm }: CloseAlertModalProps) => {
  const [notes, setNotes] = useState('');

  // Allow modal for any Custom Alert with a workplaceId
  const isCustom = isCustomAlert(alert);

  // Only fetch global actions when the alert has a globalActionId
  const { data: globalActions } = useGetGlobalActionsQuery(undefined, {
    skip: !isCustom || !(alert as Custom_Alerts)?.globalActionId,
  });

  if (!alert) return null;
  if (!isCustom) return null;

  const globalActionId = (alert as Custom_Alerts)?.globalActionId;
  const linkedGlobalAction = globalActions?.find((ga: any) => ga.id === globalActionId) ?? null;
  const alertName = alert.smpp_message || `Alert #${alert.message_id}`;
  const sourceName =
    linkedGlobalAction?.title || (alert as Custom_Alerts)?.madeBy || 'Unknown source';
  const timestamp = alert.issue_time_utc
    ? formatDateTime(alert.issue_time_utc, '2-digit')
    : 'Unknown Timestamp';

  return (
    <BaseModal
      show={show}
      handleShow={onClose}
      title="Close Alert"
      size="md"
      borderColor={undefined} // TODO: placeholder
      borderWidth="2px"
      style={undefined} // TODO: placeholder
      contentStyle={undefined} // TODO: placeholder
      backgroundColor={undefined} // TODO: placeholder
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit(notes)}
            onClick={() => onConfirm(notes.trim())}
          >
            Save &amp; Close
          </Button>
        </>
      }
    >
      <p className="mb-2">
        Are you sure you want to close the alert <strong>&apos;{alertName}&apos;</strong> created on{' '}
        <strong>{timestamp}</strong> from <strong>{sourceName}</strong>?
      </p>
      <p className="mb-3">
        To proceed, enter a note (required) below and click <strong>Save &amp; Close</strong>. To
        cancel, click <strong>Cancel</strong>.
      </p>
      <textarea
        className="form-control"
        rows={3}
        placeholder="Required: add note before closing"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
    </BaseModal>
  );
};

export default CloseAlertModal;
